/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import React, { useState, useEffect } from "react";
import { Card, Image, Form, InputGroup } from "react-bootstrap";
import { PaperClipIcon, XIcon } from "@heroicons/react/solid";
import * as S from "../../styles";
import { AiFillCloseCircle } from "react-icons/ai";
import { pretty } from "@vlinder-web/utils-react";
import * as LS from "./styles";

export interface IChoosePhotoProps {
  id: string;
  label?: string;
  labelColor?: string;
  errorText?: string;
  title?: string;
  maxSize?: number;
  bgColor?: string;
  value?: string;
  removeLabel?: string;
  required?: boolean;
  chooseImageLabel?: string;
  chooseImageTooltip?: string;
  multiple?: boolean;
  onCompChange?(
    id: string,
    file: {
      name: string;
      type: string;
      size: string;
      base64: string;
      file: string;
    }
  ): void;
  errorMsg?: string;
  hasError?: boolean;
}

export const ChoosePhoto = ({
  id,
  label,
  labelColor,
  errorText,
  required,
  maxSize,
  title,
  removeLabel,
  value,
  bgColor,
  errorMsg,
  chooseImageLabel,
  chooseImageTooltip,
  multiple,
  onCompChange,
  hasError,
}: IChoosePhotoProps) => {
  const [selectedPhoto, setSelectedPhoto] = useState<any>(value);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (value) {
      setSelectedPhoto(value);
      onCompChange && onCompChange(id, value);
    }
  }, [value]);

  let fileInputRef: any = null;

  const resetFileInput = () => {
    if (fileInputRef) {
      fileInputRef.value = null;
    }
  };

  const onChange = (e: any) => {
    let files = e.target.files;
    const allFiles: any = [];
    for (let i = 0; i < files.length; i++) {
      let file = files[i];
      if (file.size >= maxSize) {
        setError(true);
        setSelectedPhoto(null);
        onCompChange && onCompChange(id, null);
      } else {
        setError(false);
        let reader = new FileReader();
        reader.readAsDataURL(file);

        reader.onload = () => {
          let fileInfo = {
            name: file.name,
            type: file.type,
            size: Math.round(file.size / 1000) + " kB",
            base64: reader.result,
            file: file,
          };
          allFiles.push(fileInfo);
          if (allFiles.length === files.length) {
            if (multiple) {
              onCompChange && onCompChange(id, allFiles);
            } else {
              onCompChange && onCompChange(id, allFiles[0]);
            }
            setSelectedPhoto(allFiles[0]?.base64);
          }
        };
      }
    }
  };

  const onRemove = () => {
    setSelectedPhoto(null);
    setError(false);
    resetFileInput();
    onCompChange && onCompChange(id, null);
  };

  const _renderLabelIf = () => {
    if (label) {
      return (
        <S.FormLabel color={labelColor}>
          {label}
          {required && <sup style={{ color: "red" }}>*</sup>}
        </S.FormLabel>
      );
    }
    return null;
  };

  const _renderErrorTextIf = () => {
    if (hasError && errorText) {
      return (
        <S.FeedbackOtherLabel type="tooltip">{errorText}</S.FeedbackOtherLabel>
      );
    }
    return null;
  };

  return (
    <>
      <Form.Group id={id} className="formgroup">
        {_renderLabelIf()}
        <InputGroup>
          <Card border="0" className="mb-4">
            <Card.Body>
              <div className="d-flex align-items-center">
                {selectedPhoto && (<div style={LS.FileContainer}>
                  <Image fluid rounded src={selectedPhoto} />
                  <AiFillCloseCircle
                    size={22}
                    onClick={onRemove}
                    style={LS.FileRemoveIcon}
                  />
                </div>)}
                <div className="file-field">
                  <div className="d-flex justify-content-xl-center ms-xl-3">
                    <div className="d-flex">
                      <PaperClipIcon className="icon text-gray-500 me-2" />
                      <input
                        type="file"
                        accept="image/jpeg, image/gif, image/png"
                        onChange={onChange}
                        ref={(ref) => (fileInputRef = ref)}
                      />
                      <div className="d-md-block text-left">
                        <div className="fw-normal text-dark mb-1">
                          {chooseImageLabel}
                        </div>
                        <div className="text-gray small">
                          {chooseImageTooltip}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </InputGroup>
      </Form.Group>
      {error && (
        <div style={{ color: "red", marginTop: "5px", fontSize: "12px" }}>
          {errorMsg}
        </div>
      )}
    </>
  );
};

ChoosePhoto.defaultProps = {
  required: false,
  title: "Select Photo",
  value:
    "https://s3.ap-south-1.amazonaws.com/io.vlinder.images/placeholder.jpeg",
  chooseImageLabel: "Click here to choose Image",
  removeLabel: "Remove Profile",
  chooseImageTooltip: "JPG, GIF or PNG. Max size of 800K",
  multiple: false,
  maxSize: 800000,
  errorMsg: "*File size exceeds the limit",
};
